import PropTypes from 'prop-types';

const PackagingShape = PropTypes.shape({
  _id: PropTypes.string.isRequired,
  active: PropTypes.bool.isRequired,
  created_at: PropTypes.string.isRequired,
  default: PropTypes.bool.isRequired,
  default_international: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  product_line_id: PropTypes.string.isRequired,
  system_name: PropTypes.string.isRequired,
  updated_at: PropTypes.string.isRequired
});

// Dimensions shape
const DimensionsShape = PropTypes.shape({
  in: PropTypes.number.isRequired,
  cm: PropTypes.number.isRequired
});

// Weight shape  
const WeightShape = PropTypes.shape({
  lbs: PropTypes.number.isRequired,
  kg: PropTypes.number.isRequired
});

// Product shape
const ProductShape = PropTypes.shape({
  product_id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  display_name: PropTypes.string.isRequired,
  public: PropTypes.bool.isRequired,
  sku: PropTypes.string.isRequired,
  product_line: PropTypes.string.isRequired,
  product_line_filter_key: PropTypes.string.isRequired,
  length: DimensionsShape.isRequired,
  max_dimensions: DimensionsShape.isRequired,
  product_line_domestic_countries: PropTypes.arrayOf(PropTypes.string).isRequired,
  weight: WeightShape.isRequired,
  price_cents: PropTypes.number.isRequired,
  admin_only: PropTypes.bool.isRequired,
  pro_only: PropTypes.bool.isRequired
});

// Rate/Product Line shape
const RateShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  packaging: PropTypes.arrayOf(PackagingShape).isRequired,
  products: PropTypes.arrayOf(ProductShape).isRequired
});

// Location shape
const LocationShape = PropTypes.shape({
  country_code: PropTypes.string.isRequired,
  country_name: PropTypes.string.isRequired,
  state: PropTypes.string.isRequired,
  city: PropTypes.string.isRequired,
  address_1: PropTypes.string.isRequired,
  address_gtm: PropTypes.string.isRequired,
  address_sailthru: PropTypes.string.isRequired,
  zip: PropTypes.string.isRequired
});

// Main Pricing shape
export const Pricing = PropTypes.shape({
  brand_id: PropTypes.string.isRequired,
  rates: PropTypes.arrayOf(RateShape).isRequired,
  origin: LocationShape.isRequired,
  destination: LocationShape.isRequired,
  country_padding_days: PropTypes.number.isRequired,
  messages: PropTypes.object.isRequired,
  drop_off: PropTypes.bool.isRequired,
  combine: PropTypes.bool.isRequired
});

export const PartnerConfig = PropTypes.shape({
  partnerId: PropTypes.string.isRequired,
  brand: PropTypes.string,
  subdomain: PropTypes.string,
  couponCode: PropTypes.string,
  termsConditionsText: PropTypes.string,
  defaultCTAPath: PropTypes.string,
  showNavExtraLinks: PropTypes.bool,
  defaultMenuItems: PropTypes.array,
  logoClasses: PropTypes.string,
  supportPhoneNumber: PropTypes.string,
  images: PropTypes.shape({
    heroBackgroundPosition: PropTypes.string,
    backgroundMobile: PropTypes.string.isRequired,
    backgroundDesktop: PropTypes.string.isRequired,
    navLogo: PropTypes.string.isRequired,
    footerLogo: PropTypes.string.isRequired,
    shipPlayLogo: PropTypes.string
  }).isRequired,
  hero: PropTypes.shape({
    header: PropTypes.string.isRequired,
    subheader: PropTypes.string.isRequired,
    quickQuoteTitle: PropTypes.string,
    quickQuoteShipFromTitle: PropTypes.string,
    quickQuoteShipToTitle: PropTypes.string,
    breakText: PropTypes.string.isRequired,
    buttonText: PropTypes.string.isRequired,
    customClass: PropTypes.string
  }).isRequired,
  shipOverview: PropTypes.shape({
    title: PropTypes.string.isRequired,
    showLogo: PropTypes.bool,
    reasons: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired
    })).isRequired,
    description: PropTypes.arrayOf(PropTypes.string).isRequired,
    customClass: PropTypes.string
  }).isRequired,
  features: PropTypes.shape({
    header: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({
      image: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired
    })).isRequired,
    customClass: PropTypes.string
  }).isRequired,
  showShipPlayPromise: PropTypes.bool,
  shipPlayPromise: PropTypes.shape({
    customClass: PropTypes.string
  }),
  footer: PropTypes.shape({
    showTerms: PropTypes.bool,
    termsTitle: PropTypes.string,
    defaultTerms: PropTypes.string,
    showNewsletter: PropTypes.bool,
    customClass: PropTypes.string,
    supportPhoneNumber: PropTypes.string,
  }).isRequired,
  footerLogoSize: PropTypes.string
});

// Base shape definitions for reusable objects
export const ShippingAddress = PropTypes.shape({
  formatted_address: PropTypes.string,
  delivery_address_line: PropTypes.string,
  delivery_address_line_1: PropTypes.string,
  city: PropTypes.string,
  state: PropTypes.string,
  country_code: PropTypes.string,
  zip5: PropTypes.string,
  zip4: PropTypes.string,

  company_name: PropTypes.string,
  attention_name: PropTypes.string,
  phone_number: PropTypes.string,
  
  // Location data
  google_place_id: PropTypes.string,
  latitude: PropTypes.number,
  longitude: PropTypes.number,
  
  // Address classification
  address_type: PropTypes.string,
  
  // Configuration flags
  cruise: PropTypes.bool,
  logistics_provider_enabled: PropTypes.bool,
  
  // Additional metadata
  place_types: PropTypes.arrayOf(PropTypes.string),
  
  // Legacy field
  _id: PropTypes.string
});

export const TripTypeShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired
});

export const CartShape = PropTypes.shape({
  id: PropTypes.string,
  token: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.object)
});

export const CustomerInfoShape = PropTypes.shape({
  id: PropTypes.string,
  authToken: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  email: PropTypes.string,
  phoneNumber: PropTypes.string
});

export const LuggageTypeShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  weight: PropTypes.string.isRequired,
  dimensions: PropTypes.string.isRequired,
  price: PropTypes.number.isRequired
});

export const ShippingMethodShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  deliveryDays: PropTypes.string.isRequired,
  price: PropTypes.number.isRequired,
  description: PropTypes.string.isRequired
});

export const ServiceShape = PropTypes.shape({
  id: PropTypes.string,
  name: PropTypes.string,
  deliveryDays: PropTypes.string,
  price: PropTypes.number,
  description: PropTypes.string
});

export const InitialFormDataShape = PropTypes.shape({
  brandId: PropTypes.string,
  authToken: PropTypes.string,
  cart: CartShape,
  tripType: TripTypeShape,
  locationType: PropTypes.string,
  fromAddress: PropTypes.string,
  toAddress: PropTypes.string,
  selectedProducts: PropTypes.array,
  customerInfo: CustomerInfoShape
});

// Partner configuration shape
export const PartnerConfigShape = PropTypes.shape({
  supportPhoneNumber: PropTypes.string,
  // Add other partner config properties as needed
  partnerId: PropTypes.string,
  brandName: PropTypes.string,
  themeColor: PropTypes.string,
  logoUrl: PropTypes.string
});

// Environment enum
export const EnvironmentEnum = PropTypes.oneOf(['development', 'staging', 'production']);

// Feature flags enum
export const FeatureFlagsEnum = PropTypes.oneOf(['api', 'localStorage', 'sessionStorage', 'redirect']);

// Flow variant enum
export const FlowVariantEnum = PropTypes.oneOf(['widget', 'forward', 'standard']);

// Main ShippingFlow PropTypes
export const ShippingFlowState = {
  // Required props
  partnerConfig: PartnerConfigShape,
  flowVariant: FlowVariantEnum,
  
  // Callback functions
  onSubmit: PropTypes.func,
  onError: PropTypes.func,
  onDataLoad: PropTypes.func,
  onPageChange: PropTypes.func,
  
  // Styling props
  className: PropTypes.string,
  style: PropTypes.object,
  
  // Configuration props
  debug: PropTypes.bool,
  overridePartnerId: PropTypes.string,
  overrideEnvironment: EnvironmentEnum,
  
  // Custom data overrides
  customLuggageTypes: PropTypes.arrayOf(LuggageTypeShape),
  customShippingMethods: PropTypes.arrayOf(ShippingMethodShape),
  
  // Service selections
  selectedOutboundService: ServiceShape,
  selectedReturnService: ServiceShape,
  
  // Feature flags and testing
  disabledFeatures: PropTypes.arrayOf(FeatureFlagsEnum),
  initialFormData: InitialFormDataShape,

  onStartBooking: PropTypes.func,
  onExitBooking: PropTypes.func
};

// Default props for ShippingFlow
export const ShippingFlowDefaultState = {
  buttonText: 'Unlock effortless travel',
  breakText: 'Rates start at $65',
  fromTitle: 'From Cruise',
  toTitle: 'To Cruise',
  supportPhoneNumber: '(855) 645-2085',
  onSubmit: undefined,
  onError: undefined,
  onDataLoad: undefined,
  onPageChange: undefined,
  className: '',
  style: {},
  debug: false,
  overridePartnerId: undefined,
  overrideEnvironment: undefined,
  customLuggageTypes: undefined,
  customShippingMethods: undefined,
  selectedOutboundService: undefined,
  selectedReturnService: undefined,
  disabledFeatures: [],
  selectedProducts: [],
  initialFormData: undefined
};

// Utility function to create reusable PropType shapes for forms
export const createDataShape = (additionalFields = {}) => {
  return PropTypes.shape({
    brandId: PropTypes.string,
    authToken: PropTypes.string,
    cart: CartShape,
    tripType: TripTypeShape,
    locationType: PropTypes.string,
    customerInfo: CustomerInfoShape,
    ...additionalFields
  });
};

export const ShippingLeg = PropTypes.shape({
  id: PropTypes.string,
  index: PropTypes.number,
  fromAddress: ShippingAddress,
  toAddress: ShippingAddress,
  selectedProducts: PropTypes.array,
  pricing: Pricing,
  shipDate: Date,
  deliveryDate: Date
});

export const ShippingFlowData = PropTypes.shape({
  cart: CartShape,
  tripType: TripTypeShape,
  locationType: PropTypes.string,
  customerInfo: CustomerInfoShape,
  legs: PropTypes.arrayOf(ShippingLeg),
});

// Factory function for creating callback PropTypes
export const createCallbackProps = (callbacks = []) => {
  const props = {};
  callbacks.forEach(callback => {
    props[callback] = PropTypes.func;
  });
  return props;
};

// Common validation patterns
export const CommonValidations = {
  email: function(props, propName, componentName) {
    const value = props[propName];
    if (value && !/\S+@\S+\.\S+/.test(value)) {
      return new Error(
        `Invalid prop \`${propName}\` of value \`${value}\` supplied to \`${componentName}\`, expected a valid email address.`
      );
    }
  },
  
  phoneNumber: function(props, propName, componentName) {
    const value = props[propName];
    if (value && !/^\+?[\d\s\-\(\)]+$/.test(value)) {
      return new Error(
        `Invalid prop \`${propName}\` of value \`${value}\` supplied to \`${componentName}\`, expected a valid phone number.`
      );
    }
  },
  
  positiveNumber: function(props, propName, componentName) {
    const value = props[propName];
    if (value !== undefined && (typeof value !== 'number' || value < 0)) {
      return new Error(
        `Invalid prop \`${propName}\` of value \`${value}\` supplied to \`${componentName}\`, expected a positive number.`
      );
    }
  }
};
